import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AccountCircle as AccountIcon, Add as AddIcon, Public as PublicIcon, Lock as PrivateIcon } from '@mui/icons-material';
import { FormControl, Select, MenuItem, CircularProgress, Box, Typography, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';

import { width } from '../../../node_modules/@mui/system/index';
import { getAvailableIntegrations, createIntegration, getIntegrationTools } from '../../store/Integration';

// Helper function to generate field label from field name
const getFieldLabel = (fieldName) => {
  return fieldName
    .replace(/^[A-Z]+_/, '') // Remove prefix like TWILIO_, WHATSAPP_
    .replace(/_/g, ' ')       // Replace underscores with spaces
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
};

// Helper function to determine field type from field name
const getFieldType = (fieldName) => {
  const lowerName = fieldName.toLowerCase();
  if (lowerName.includes('token') || lowerName.includes('secret') || lowerName.includes('auth') || lowerName.includes('password'))
    return 'password';
  else return 'text';
};

/**
 * IntegrationSelector Component
 *
 * Reusable dropdown for selecting integrations with support for:
 * - User's own integrations (private + public)
 * - Workspace public integrations (shared by other users)
 * - "Connect New Account" option
 *
 * @param {string} toolName - Name of the integration tool (e.g., "Gmail", "Twilio SMS", "WhatsApp")
 * @param {string} value - Currently selected integration ID
 * @param {function} onChange - Callback when selection changes (integrationId) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {string} placeholder - Placeholder text
 * @param {function} onConnectNew - Optional callback for "Connect New" button
 */
const IntegrationSelector = ({ toolName, value, onChange, disabled = false, placeholder = 'Select account', onConnectNew }) => {

  const dispatch = useDispatch();
  
  // Get available integrations and tools from Redux store
  const tools = useSelector(state => state['Integration']?.['tools']?.['data']);
  const loading = useSelector(state => state['Integration']?.['availableIntegrations']?.['loading']);
  const availableIntegrations = useSelector( state => state['Integration']?.['availableIntegrations']?.['data']?.[toolName]);
  
  const ownIntegrations = availableIntegrations?.['ownIntegrations'] || [];
  const publicIntegrations = availableIntegrations?.['publicIntegrations'] || [];

  const [credentials, setCredentials] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);

  // Get tool data for dynamic credential fields
  const tool = tools?.find(t => t['name']?.toLowerCase() === toolName?.toLowerCase());

  // Generate credential fields dynamically from tool's requiredFields
  const credentialFields = React.useMemo(() => {
    if (!tool?.['requiredFields'] || tool?.['requiredFields'].length === 0) return [];

    return tool['requiredFields'].map(field => {
      // Handle both string and object formats for requiredFields
      const name = typeof field === 'object' ? field['name'] : field;
      const type = typeof field === 'object' && field['type'] ? field['type'] : getFieldType(name);
      const label = typeof field === 'object' && field['label'] ? field['label'] : getFieldLabel(name);

      return { name, label, type, required: typeof field === 'object' ? (field.required !== false) : true }
    })
  }, [tool?.['requiredFields']]);

  // Fetch tools if not loaded
  useEffect(() => {
    if (!tools || tools.length === 0) dispatch(getIntegrationTools());
  }, [dispatch, tools]);

  // Fetch available integrations when component mounts or toolName changes
  useEffect(() => {
    if (toolName) dispatch(getAvailableIntegrations(toolName));
  }, [toolName, dispatch]);

  // Sync local value with prop value
  useEffect(() => { setLocalValue(value || ''); }, [value]);

  // Auto-select primary integration, or first available if no primary
  useEffect(() => {
    if (!value && (ownIntegrations.length > 0 || publicIntegrations.length > 0)) {
      // First, look for primary integration in own integrations
      const primaryOwn = ownIntegrations.find(int => int.isPrimary);
      if (primaryOwn) {
        setLocalValue(primaryOwn._id);
        onChange && onChange(primaryOwn._id);
        return;
      }

      // Then, look for primary in public integrations
      const primaryPublic = publicIntegrations.find(int => int.isPrimary);
      if (primaryPublic) {
        setLocalValue(primaryPublic._id);
        onChange && onChange(primaryPublic._id);
        return;
      }

      // Fall back to first own integration
      if (ownIntegrations.length > 0) {
        setLocalValue(ownIntegrations[0]._id);
        onChange && onChange(ownIntegrations[0]._id);
        return;
      }

      // Finally, fall back to first public integration
      if (publicIntegrations.length > 0) {
        setLocalValue(publicIntegrations[0]._id);
        onChange && onChange(publicIntegrations[0]._id);
      }
    }
  }, [ownIntegrations, publicIntegrations, value, onChange]);

  // Handle successful integration connection
  const handleIntegrationSuccess = () => {
    setCredentials({});
    setConnecting(false);
    setShowCredentialsDialog(false);
    if (toolName) dispatch(getAvailableIntegrations(toolName));
  };

  // Handle failed integration connection
  const handleIntegrationFailure = (error) => {
    setConnecting(false);
    alert(error || `Failed to connect ${toolName}`);
  };

  const [open, setOpen] = useState(false);

  // Handle connecting a new integration
  const handleConnect = () => {
    setOpen(false);
    if (onConnectNew) {
      onConnectNew();
      return;
    }

    // Determine auth method from tool data
    const authMethod = tool?.authMethod || 'oAuth2';

    // For API Key auth: Show credentials dialog
    if (authMethod === 'apiKey' && credentialFields.length > 0) {
      setShowCredentialsDialog(true);
      setCredentials({});
    }
    // For OAuth2 auth: Trigger OAuth flow directly
    else {
      setConnecting(true);
      dispatch(createIntegration({
        name: toolName,
        authType: 'oAuth2',
        onSuccess: handleIntegrationSuccess,
        onFailure: handleIntegrationFailure
      }));

      // Reset connecting state after a delay
      setTimeout(() => setConnecting(false) , 3000);
    }
  }

  // Handle credential input change
  const handleCredentialChange = (fieldName, value) => setCredentials(prev => ({ ...prev, [fieldName]: value }));

  // Handle credentials submission
  const handleCredentialsSubmit = () => {
    const requiredFields = credentialFields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !credentials[f.name]);

    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setConnecting(true);
    dispatch(createIntegration({
      name: toolName,
      authType: 'apiKey',
      ...credentials,
      onSuccess: handleIntegrationSuccess,
      onFailure: handleIntegrationFailure
    }));
  };

  // Handle dialog close
  const handleDialogClose = () => {
    if (!connecting) {
      setShowCredentialsDialog(false);
      setCredentials({});
    }
  };

  const handleChange = (event) => {
    const selectedValue = event['target']['value'];
    if (selectedValue === '__connect_new__') {
      event.preventDefault();
      return;
    }

    setLocalValue(selectedValue);
    if (onChange) onChange(selectedValue);
    else console.warn('[IntegrationSelector] onChange is not provided!');
  };

  // Get display label for an integration
  const getIntegrationLabel = (integration) => {
    // Try to extract meaningful identifier (email, phone, etc.)
    const identifier = integration.tokens?.email || integration.publicKey || integration.whatsappAccount?.phoneNumber || integration.name || 'Account';
    return identifier;
  };

  // Get owner display name
  const getOwnerName = (integration) => {
    const user = integration.userId;
    if (!user) return 'Unknown User';

    const firstName = user['firstName'] || '';
    const lastName = user['lastName'] || '';
    return `${firstName} ${lastName}`.trim() || user.email || 'Unknown User';
  };

  const totalIntegrations = ownIntegrations.length + publicIntegrations.length;

  return (
    <>
      <FormControl className="private-icon" fullWidth size="small" disabled={disabled || loading}>
        <Select
          open={open}
          displayEmpty
          value={localValue}
          onChange={handleChange}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          MenuProps={{ 
            PaperProps: { style: { maxHeight: 400 } },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' }
          }}
          renderValue={(selected) => {
            if (!selected) return <Typography variant="body2" color="textSecondary"> {loading ? 'Loading...' : placeholder} </Typography>

            // Find the selected integration
            const selectedIntegration = [...ownIntegrations, ...publicIntegrations].find(int => int._id === selected);
            if (!selectedIntegration) return <Typography variant="body2" color="textSecondary"> {placeholder} </Typography>

            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedIntegration['isOwned'] 
                  ? <PrivateIcon sx={{ fontSize: 10, color: 'var(--text-color)', width: '10px' }} />
                  : <PublicIcon sx={{ fontSize: 10, color: 'success.main', width: '10px' }} /> 
                }
                <Typography variant="body2">
                  {getIntegrationLabel(selectedIntegration)}
                  {!selectedIntegration['isOwned'] &&
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }} >
                      (by {getOwnerName(selectedIntegration)})
                    </Typography>
                  }
                </Typography>
              </Box>
            );
          }}
        >
          {loading ? (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'center' }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading integrations...</Typography>
              </Box>
            </MenuItem>
          ) : totalIntegrations === 0 ? (
            <>
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  No {toolName} accounts connected
                </Typography>
              </MenuItem>

              {/* Connect New Account Option - shown when no accounts exist */}
              <MenuItem
                sx={{ p: 1.5 }}
                value="__connect_new__"
                // onClickCapture={(e) => {
                //   e.stopPropagation();
                //   e.preventDefault();
                //   handleConnect();
                // }}
              >
                <button
                  variant="contained"
                  disabled={connecting}
                  className="btn-style-new"
                  startIcon={connecting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AddIcon />}
                  sx={{ fontWeight: 500, textTransform: 'none', pointerEvents: 'auto', justifyContent: 'flex-start' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnect();
                  }}
                >
                  {connecting ? `Connecting ${toolName}...` : `Connect ${toolName}`}
                </button>
              </MenuItem>
            </>
          ) : (
            <>
              {/* Own Integrations Section */}
              {ownIntegrations.length > 0 && (
                <>
                  <MenuItem disabled sx={{ opacity: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--primary-color)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Your Accounts
                    </Typography>
                  </MenuItem>
                  {ownIntegrations.map((integration) => (
                    <MenuItem
                      key={integration['_id']}
                      value={integration['_id']}
                      onClick={(e) => {
                        setLocalValue(integration['_id']);
                        if (onChange) {
                          setOpen(false);
                          onChange(integration['_id']);
                        }
                        setOpen(false);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <PrivateIcon sx={{ fontSize: 18, color: 'var(--text-color)', width: '10px' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {getIntegrationLabel(integration)}
                            {integration['isPrimary'] &&
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'var(--primary-color)', fontWeight: 500 }} >
                                (Default)
                              </Typography>
                            }
                          </Typography>
                          {integration.visibilityType === 'public' &&
                            <Typography variant="caption" color="success.main"> • Shared with workspace </Typography>
                          }
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </>
              )}

              {/* Divider between sections */}
              {ownIntegrations.length > 0 && publicIntegrations.length > 0 && <Divider sx={{ my: 1 }} /> }

              {/* Public Integrations Section */}
              {publicIntegrations.length > 0 && (
                <>
                  <MenuItem disabled sx={{ opacity: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Workspace Shared Accounts
                    </Typography>
                  </MenuItem>
                  {publicIntegrations.map((integration) => (
                    <MenuItem
                      key={integration['_id']}
                      value={integration['_id']}
                      onClick={(e) => {
                        setLocalValue(integration['_id']);
                        if (onChange) {
                          setOpen(false);
                          onChange(integration['_id']);
                        }
                        setOpen(false);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <PublicIcon sx={{ fontSize: 18, color: 'success.main', width: '10px' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2"> {getIntegrationLabel(integration)} </Typography>
                          <Typography variant="caption" color="textSecondary"> Connected by {getOwnerName(integration)} </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </>
              )}

              {/* Connect New Account Option */}
              <MenuItem
                value="__connect_new__"
                onClickCapture={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleConnect();
                }}
                sx={{ p: 1.5 }}
              >
                <button
                  variant="contained"
                  className="btn-style-new"
                  disabled={connecting}
                  startIcon={connecting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AddIcon />}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500, pointerEvents: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnect();
                  }}
                >
                  {connecting ? `Connecting ${toolName}...` : `Connect ${toolName}`}
                </button>
              </MenuItem>
            </>
          )}
        </Select>
      </FormControl>

      {/* Credentials Dialog for Twilio and WhatsApp */}
      {(() => {
        console.log('[IntegrationSelector] Dialog render check:', {
          showCredentialsDialog,
          credentialFieldsLength: credentialFields.length,
          shouldRender: showCredentialsDialog && credentialFields.length > 0
        });
        return null;
      })()}
      {showCredentialsDialog && credentialFields.length > 0 && (
        <Dialog
          fullWidth
          maxWidth="sm"
          onClose={handleDialogClose}
          open={showCredentialsDialog}
          className="modal-dialog-set"
          disableEscapeKeyDown={connecting}
        >
          <DialogTitle>Connect {toolName}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Enter your {toolName} credentials to connect your account.
              </Alert>

              {credentialFields.map((field) => (
                <div className="group-from">
                  <label>{field.label}</label>
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    key={field['name']}
                    variant="outlined"
                    type={field['type']}
                    disabled={connecting}
                    required={field['required']}
                    placeholder={field['placeholder']}
                    value={credentials[field['name']] || ''}
                    onChange={(e) => handleCredentialChange(field['name'], e['target']['value'])}
                  />
                </div>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <div className="group-from btn-groups">
              <button
                size="small"
                variant="outlined"
                disabled={connecting}
                onClick={handleDialogClose}
                className="btn-style-border mr-3"
              >
                Cancel
              </button>
              <button
                size="small"
                variant="contained"
                disabled={connecting}
                className="btn-style-new"
                onClick={handleCredentialsSubmit}
              >
                {connecting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null} {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default IntegrationSelector;